/**
 * featured-stores.js — <featured-stores> component (MA-1369).
 *
 * Drives the Featured Stores page (sections/featured-stores.liquid) for the
 * TWN / HKG markets:
 *
 *   1. Fetches every featured-store record for the active market on connect
 *      (`@theme/query-featured-stores`, page segment -1 = all records) so the
 *      displayed Cashback/IBV rates always reflect the current active rate at
 *      page load — never a cached page. bfcache restores re-fetch for the
 *      same reason.
 *   2. Renders the card grid via `renderFeaturedStoreCard`
 *      (`@theme/ng-featured-store-card`), which reuses the shared partner
 *      store card treatment.
 *   3. Paginates client-side when there are more records than fit one page
 *      (no "see all" link per the BR) using the shared pagination controls
 *      (`@theme/hydrate-pagination`) with separate desktop/mobile page sizes.
 *
 * The section supplies localized strings via data-i18n-* attributes and
 * pagination labels via data-label-* on the pagination nav ref — no hardcoded
 * user-facing English here.
 */

import { Component } from "@theme/component";
import { queryFeaturedStores } from "@theme/query-featured-stores";
import { renderFeaturedStoreCard } from "@theme/ng-featured-store-card";
import { renderStoreCardSkeletons } from "@theme/ng-store-card";
import {
  renderPagination,
  bindPaginationEvents,
  readPaginationLabels,
  scrollToWithHeaderOffset,
} from "@theme/hydrate-pagination";
import { mediaQueryLarge } from "@theme/utilities";

/**
 * @typedef {object} Refs
 * @property {HTMLElement} grid - The store card <ul>.
 * @property {HTMLElement} status - The loading / empty / error status line.
 * @property {HTMLElement} paginationNav - The pagination <nav>.
 *
 * @extends {Component<Refs>}
 */
class FeaturedStores extends Component {
  requiredRefs = ["grid", "status", "paginationNav"];

  /** @type {Array<Record<string, any>>} */
  #stores = [];

  /** 1-based current page. */
  #page = 1;

  /** True once the first fetch settled (ok or error). */
  #loaded = false;

  /** @type {AbortController | null} */
  #abortController = null;

  #onBreakpointChange = () => {
    if (this.#loaded && this.#stores.length) this.#renderPage({ scroll: false });
  };

  /**
   * bfcache restores show the DOM exactly as it was left — including
   * potentially stale promotional rates. Re-fetch so rates always reflect
   * the current active values (MA-1369 AC).
   *
   * @param {PageTransitionEvent} event
   */
  #onPageShow = event => {
    if (event.persisted) this.#load();
  };

  connectedCallback() {
    super.connectedCallback();
    bindPaginationEvents(this.refs.paginationNav, page => this.#onPageChange(page));
    mediaQueryLarge.addEventListener("change", this.#onBreakpointChange);
    window.addEventListener("pageshow", this.#onPageShow);
    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener("change", this.#onBreakpointChange);
    window.removeEventListener("pageshow", this.#onPageShow);
    this.#abortController?.abort();
  }

  /** @returns {number} Cards per page for the active breakpoint. */
  get #pageSize() {
    const attr = mediaQueryLarge.matches ? "data-page-size" : "data-page-size-mobile";
    const size = parseInt(this.getAttribute(attr) || "", 10);
    return Number.isFinite(size) && size > 0 ? size : 25;
  }

  /** @param {string} key @returns {string} */
  #i18n(key) {
    return this.getAttribute(`data-i18n-${key}`) || "";
  }

  /**
   * Shows the status line (loading / empty / error) and hides the grid, or
   * vice versa when `message` is empty.
   *
   * @param {string} message
   */
  #setStatus(message) {
    const { grid, status } = this.refs;
    status.textContent = message;
    status.hidden = !message;
    grid.hidden = Boolean(message);
  }

  /**
   * Paints store-card skeletons into the grid while the fetch is in flight,
   * instead of hiding the grid behind a "Loading…" text line. Real cards
   * replace them in #renderPage. Loading visual only — no behaviour change.
   */
  #showLoading() {
    const { grid, status } = this.refs;
    status.hidden = true;
    status.textContent = "";
    grid.hidden = false;
    grid.setAttribute("aria-busy", "true");
    grid.innerHTML = renderStoreCardSkeletons(Math.min(this.#pageSize || 8, 12));
  }

  async #load() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#showLoading();
    this.refs.paginationNav.hidden = true;

    let result;
    try {
      result = await queryFeaturedStores({ signal: this.#abortController.signal });
    } catch (error) {
      if (error.name === "AbortError") return;
      throw error;
    }

    this.#loaded = true;

    if (!result.ok) {
      console.warn("[featured-stores]", result.error.message);
      this.#setStatus(this.#i18n("error"));
      return;
    }

    // The service returns records pre-curated for the market; honour its
    // merchandising order via sortOrder.
    this.#stores = [...result.data.stores].sort((a, b) => (Number(a?.sortOrder) || 0) - (Number(b?.sortOrder) || 0));

    if (!this.#stores.length) {
      this.#setStatus(this.#i18n("empty"));
      return;
    }

    this.#page = 1;
    this.#renderPage({ scroll: false });
  }

  /** @param {number} page */
  #onPageChange(page) {
    const totalPages = Math.max(1, Math.ceil(this.#stores.length / this.#pageSize));
    this.#page = Math.min(Math.max(1, page), totalPages);
    this.#renderPage({ scroll: true });
  }

  /** @param {{ scroll: boolean }} options */
  #renderPage({ scroll }) {
    const pageSize = this.#pageSize;
    const totalPages = Math.max(1, Math.ceil(this.#stores.length / pageSize));
    if (this.#page > totalPages) this.#page = totalPages;

    const start = (this.#page - 1) * pageSize;
    const pageStores = this.#stores.slice(start, start + pageSize);

    this.#setStatus("");
    this.refs.grid.innerHTML = pageStores.map(store => renderFeaturedStoreCard(store)).join("");
    this.refs.grid.removeAttribute("aria-busy");

    const { paginationNav } = this.refs;
    paginationNav.innerHTML = renderPagination(this.#page, totalPages, readPaginationLabels(paginationNav));
    paginationNav.hidden = totalPages <= 1;

    if (scroll) scrollToWithHeaderOffset(this);
  }
}

if (!customElements.get("featured-stores")) {
  customElements.define("featured-stores", FeaturedStores);
}